import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Sun, Moon, Bell, Settings, MessageCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import ezerLogo from "@/assets/ezer-logo.png"

interface HeaderProps {
  title?: string
  showSettings?: boolean
  className?: string
}

export function Header({ title = "Ezer", showSettings = true, className }: HeaderProps) {
  const navigate = useNavigate()
  const { theme, toggleTheme } = useTheme()
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    const goOnline = () => setIsOnline(true)
    const goOffline = () => setIsOnline(false)
    window.addEventListener("online", goOnline)
    window.addEventListener("offline", goOffline)
    return () => {
      window.removeEventListener("online", goOnline)
      window.removeEventListener("offline", goOffline)
    }
  }, [])

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        "safe-area-top",
        className
      )}
    >
      <div className="flex h-14 items-center justify-between px-4">
        {/* Logo and Title */}
        <div className="flex items-center gap-3">
          <img src={ezerLogo} alt="Ezer" className="h-12 w-12 rounded-xl object-contain dark:brightness-0 dark:invert" />
          <span className="font-bold text-xl tracking-tight">{title}</span>
        </div>

        {/* Status and Actions */}
        <div className="flex items-center gap-2">
          {/* Online Status (read-only indicator) */}
          <div
            className={cn(
              "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium",
              isOnline
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "border-muted bg-muted/50 text-muted-foreground"
            )}
          >
            <span
              className={cn(
                "h-2 w-2 rounded-full",
                isOnline ? "bg-green-400" : "bg-muted-foreground"
              )}
            />
            {isOnline ? "Online" : "Offline"}
          </div>

          {/* Notifications */}
          <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} aria-label="Notifications">
            <Bell className="h-5 w-5" />
          </Button>

          {/* City Chat */}
          <Button variant="ghost" size="icon" onClick={() => navigate("/chat")} aria-label="City Chat">
            <MessageCircle className="h-5 w-5" />
          </Button>

          {/* Theme Toggle */}
          <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === "dark" ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>

          {/* Settings (desktop only) */}
          {showSettings && (
            <Button variant="ghost" size="icon" className="hidden md:flex" onClick={() => navigate("/settings")} aria-label="Settings">
              <Settings className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    </header>
  )
}
